/* The ACTIVE IDENTITY WORKSPACE (org-level container with its own API keys and
 * gateway data) — not to be confused with the existing "agent workspace" UI
 * (AgentWorkspace/WorkspaceTabs), which is the per-agent dashboard area.
 *
 * WorkspaceShell resolves /w/:workspaceId and provides this context; every
 * absolute in-app link goes through useWsPath() so it lands in the active
 * workspace, and the chrome's route matching runs on the stripped pathname
 * from useWorkspaceRelativePathname(). */

import { createContext, useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Workspace } from "../identity";

export interface WorkspaceContextValue {
  /** The validated active workspace. */
  workspace: Workspace;
  /** All workspaces the viewer can access (drives the switcher). */
  workspaces: Workspace[];
  /** Re-fetch the workspaces list (e.g. after creating one). */
  refetchWorkspaces: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export const WorkspaceProvider = WorkspaceContext.Provider;

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }

  return ctx;
}

/** Like useWorkspace, but null outside the provider — for components that are
 *  also rendered shell-less (e.g. the auth module's user block in tests). */
export function useOptionalWorkspace(): WorkspaceContextValue | null {
  return useContext(WorkspaceContext);
}

/** Prefixes a root-relative app path with the active workspace:
 *  ws("/configs") → "/w/<id>/configs", ws("/") → "/w/<id>". */
export function useWsPath(): (path: string) => string {
  const { workspace } = useWorkspace();
  const id = workspace.id;
  return useCallback(
    (path: string) => `/w/${encodeURIComponent(id)}${path === "/" ? "" : path}`,
    [id],
  );
}

/** Navigate within the workspace, preserving the query string so URL-borne
 *  filters (range, scope, …) survive the page move. Bare navigate() for
 *  workspace switches / sign-out, which should reset filters. */
export function useWsNavigate(): (path: string) => void {
  const ws = useWsPath();
  const navigate = useNavigate();
  const { search } = useLocation();
  return useCallback(
    (path: string) => navigate(`${ws(path)}${search}`),
    [ws, navigate, search],
  );
}

/** The pathname with the /w/:workspaceId prefix stripped, so the chrome's
 *  existing string matching ("/", "/agents/…", "/configs") keeps working. */
export function useWorkspaceRelativePathname(): string {
  const { workspace } = useWorkspace();
  const { pathname } = useLocation();
  const prefix = `/w/${encodeURIComponent(workspace.id)}`;
  if (pathname === prefix) return "/";

  return pathname.startsWith(`${prefix}/`)
    ? pathname.slice(prefix.length)
    : pathname;
}
